import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Box, Button, Chip, IconButton, Link, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import ArrowBack from '@mui/icons-material/ArrowBack'
import BusinessOutlined from '@mui/icons-material/BusinessOutlined'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  COMPANY_PROFILE_CONFIRM_PERMISSION,
  COMPANY_PROFILE_WRITE_PERMISSION,
  companyProfileKey,
  confirmCompanyProfile,
  fetchCompanyProfile,
  MAX_LOCAIS,
  MERCADOS,
  PORTES,
  preenchidos,
  PROFILE_FIELDS,
  saveCompanyProfile,
  SETORES,
  TIPOS_LOCAL,
  TURNOS,
  UFS,
  type CompanyProfile,
  type Local,
  type ProfileWithStatus,
} from '../api/companyProfile'
import { gerarTextoPesquisa, lerResultado, type ResultadoLido } from '../api/profileResearch'
import { fetchMe, ME_QUERY_KEY } from '../api/session'
import { getWorkTenantId } from '../api/workTenant'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { t } from '../i18n'

/**
 * s65 / PE (Marcelo 22/10/2026 20:10, "APROVADO PE ; E1 sim ; E2 A ; E3 sim ; E4 sim ; E5 sim") -- "Empresa": o perfil
 * da empresa em que se esta trabalhando (a do seletor do consultor ou a da sessao). E1: os 6 campos de antes + produtos,
 * processos, unidades, turnos, certificacoes e mercado. E2 A: SO o consultor grava (`tenant.company_profile.set`); o RH
 * do cliente ve, sem editar. E3: aviso de nao escrever nomes -- e o servidor troca o nome da empresa por [EMPRESA]
 * antes de ir a IA. Rota fixa /empresa, sem dado na URL (ADR-0024 D10.1); tem "Voltar".
 *
 * s67 / PE2 (Marcelo 25/10/2026 11:55, "APROVADO PE2 ; F1-F4 sim"; G1-G4 A):
 *  - "Pesquisa do perfil" (so o consultor): gera o texto de pesquisa SEM o nome da empresa (G3) e le o resultado colado
 *    ("Campo: valor | fonte: link") por regra, SEM IA, como RASCUNHO -- nada e gravado ate "Salvar perfil".
 *  - F3 LOCAIS no lugar do texto de unidades (o texto antigo aparece enquanto nao houver locais -- G2).
 *  - F1 FONTES de cada campo, com link.
 *  - F4 CONFIRMACAO: o administrador ou o RH do cliente confirma (G1); o consultor nao (G4); o servidor zera a confirmacao
 *    quando o consultor muda o perfil (F2).
 */
const MAX_ITENS = 10

function ListaLivre({
  rotulo, novo, itens, limite, pode, onChange,
}: {
  rotulo: string
  novo: string
  itens: string[]
  limite: number
  pode: boolean
  onChange: (v: string[]) => void
}) {
  const [texto, setTexto] = useState('')
  const adiciona = () => {
    const v = texto.split(/\s+/).join(' ').trim()
    if (v && !itens.includes(v) && itens.length < MAX_ITENS) onChange([...itens, v])
    setTexto('')
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
        {`${rotulo} (${t.companyProfile.upTo} ${MAX_ITENS})`}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: pode ? 1 : 0 }}>
        {itens.length === 0 && !pode && <Typography variant="body2" color="text.secondary">—</Typography>}
        {itens.map((i) => (
          <Chip key={i} label={i} variant="outlined" color="info" onDelete={pode ? () => onChange(itens.filter((x) => x !== i)) : undefined} />
        ))}
      </Box>
      {pode && itens.length < MAX_ITENS && (
        <TextField
          size="small"
          fullWidth
          label={novo}
          value={texto}
          inputProps={{ maxLength: limite }}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              adiciona()
            }
          }}
          onBlur={adiciona}
          helperText={t.companyProfile.enterToAdd}
        />
      )}
    </Box>
  )
}

const ROTULO_CAMPO: Record<string, string> = {
  setor: t.companyProfile.setor, ramo: t.companyProfile.ramo, uf: t.companyProfile.uf, cidade: t.companyProfile.cidade,
  porte: t.companyProfile.porte, atividades: t.companyProfile.atividades, produtos: t.companyProfile.produtos,
  processos: t.companyProfile.processos, turnos: t.companyProfile.turnos, certificacoes: t.companyProfile.certificacoes,
  mercado: t.companyProfile.mercado, locais: t.companyProfile.locais,
}
const rotuloTipo = (v: string) => TIPOS_LOCAL.find(([c]) => c === v)?.[1] ?? v

/** s67 / PE2 (F3): a lista de locais -- nome, cidade, UF e tipo (fechado). */
function Locais({
  locais, antigo, pode, onChange,
}: {
  locais: Local[]
  antigo: string | null
  pode: boolean
  onChange: (v: Local[]) => void
}) {
  const [novo, setNovo] = useState<Local>({ nome: '', cidade: null, uf: null, tipo: '' })
  const inclui = () => {
    const nome = novo.nome.split(/\s+/).join(' ').trim()
    if (!nome || !novo.tipo || locais.length >= MAX_LOCAIS) return
    if (locais.some((x) => x.nome.toLowerCase() === nome.toLowerCase())) return
    onChange([...locais, { ...novo, nome, cidade: novo.cidade?.trim() || null }])
    setNovo({ nome: '', cidade: null, uf: null, tipo: '' })
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
        {`${t.companyProfile.locais} (${t.companyProfile.upTo} ${MAX_LOCAIS})`}
      </Typography>
      {antigo && locais.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {`${t.companyProfile.oldText} ${antigo}`}
        </Typography>
      )}
      <Stack spacing={0.5} sx={{ mb: pode ? 1 : 0 }}>
        {locais.length === 0 && !pode && !antigo && <Typography variant="body2" color="text.secondary">—</Typography>}
        {locais.map((x) => (
          <Box key={x.nome} sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="local">
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {`${x.nome} — ${rotuloTipo(x.tipo)}${x.cidade || x.uf ? ` — ${[x.cidade, x.uf].filter(Boolean).join('/')}` : ''}`}
            </Typography>
            {pode && (
              <IconButton size="small" aria-label={`Tirar ${x.nome}`} onClick={() => onChange(locais.filter((y) => y !== x))}>
                <CloseOutlined fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
      </Stack>
      {pode && locais.length < MAX_LOCAIS && (
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '2fr 1.5fr 0.8fr 1.5fr auto' }, alignItems: 'start' }}>
          <TextField size="small" label={t.companyProfile.localNome} value={novo.nome} inputProps={{ maxLength: 60 }}
            onChange={(e) => setNovo({ ...novo, nome: e.target.value })} />
          <TextField size="small" label={t.companyProfile.localCidade} value={novo.cidade ?? ''} inputProps={{ maxLength: 80 }}
            onChange={(e) => setNovo({ ...novo, cidade: e.target.value })} />
          <TextField select size="small" label={t.companyProfile.localUf} value={novo.uf ?? ''}
            onChange={(e) => setNovo({ ...novo, uf: e.target.value || null })} SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}>
            <option value="">—</option>
            {UFS.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </TextField>
          <TextField select size="small" label={t.companyProfile.localTipo} value={novo.tipo}
            onChange={(e) => setNovo({ ...novo, tipo: e.target.value })} SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}>
            <option value="">—</option>
            {TIPOS_LOCAL.map(([v, r]) => (
              <option key={v} value={v}>{r}</option>
            ))}
          </TextField>
          <Button variant="outlined" onClick={inclui} disabled={!novo.nome.trim() || !novo.tipo}>
            {t.companyProfile.localAdd}
          </Button>
        </Box>
      )}
    </Box>
  )
}

/** s67 / PE2 (G3): gera o texto de pesquisa (sem o nome da empresa) e le o resultado colado -- tudo na tela, sem IA. */
function Pesquisa({ onLido }: { onLido: (r: ResultadoLido) => void }) {
  const [texto, setTexto] = useState<string | null>(null)
  const [copiado, setCopiado] = useState(false)
  const [colado, setColado] = useState('')
  const [lido, setLido] = useState<ResultadoLido | null>(null)
  const copia = async () => {
    try {
      await navigator.clipboard.writeText(texto ?? '')
      setCopiado(true)
    } catch {
      setCopiado(false)
    }
  }
  return (
    <Paper variant="outlined" component="section" sx={{ p: 2 }}>
      <Typography variant="h2" sx={{ fontSize: '1.0625rem', mb: 1 }}>
        {t.companyProfile.research}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t.companyProfile.researchIntro}
      </Typography>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => { setTexto(gerarTextoPesquisa()); setCopiado(false) }}>
            {t.companyProfile.researchGenerate}
          </Button>
          {texto && (
            <Button onClick={() => void copia()}>{copiado ? t.companyProfile.researchCopied : t.companyProfile.researchCopy}</Button>
          )}
        </Box>
        {texto && (
          <TextField label={t.companyProfile.researchText} value={texto} multiline minRows={6} fullWidth size="small"
            InputProps={{ readOnly: true }} id="perfil-texto-pesquisa" />
        )}
        <TextField label={t.companyProfile.researchPaste} value={colado} multiline minRows={4} fullWidth size="small"
          onChange={(e) => setColado(e.target.value.slice(0, 8000))} id="perfil-resultado" />
        <Box>
          <Button variant="outlined" disabled={!colado.trim()} onClick={() => {
            const r = lerResultado(colado)
            setLido(r)
            onLido(r)
          }}>
            {t.companyProfile.researchRead}
          </Button>
        </Box>
        {lido && (
          <Alert severity={lido.lidos > 0 ? 'info' : 'warning'}>
            {`${lido.lidos} ${t.companyProfile.researchDraft}`}
            {lido.naoEntendidas.length > 0 && (
              <Box component="span" sx={{ display: 'block', mt: 1 }}>
                {t.companyProfile.researchUnknown}
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {lido.naoEntendidas.slice(0, 20).map((l, i) => (
                    <li key={i}>{l}</li>
                  ))}
                </Box>
              </Box>
            )}
          </Alert>
        )}
      </Stack>
    </Paper>
  )
}

/** s67 / PE2 (F4): quem confirmou e quando -- e o botao para o administrador ou o RH do cliente. */
function Confirmacao({
  perfil, podeConfirmar, sujo, vazio, onConfirmar, confirmando,
}: {
  perfil: ProfileWithStatus
  podeConfirmar: boolean
  sujo: boolean
  vazio: boolean
  onConfirmar: () => void
  confirmando: boolean
}) {
  const quando = perfil.confirmedAt
    ? new Date(perfil.confirmedAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    : ''
  return (
    <Paper variant="outlined" component="section" sx={{ p: 2 }}>
      <Typography variant="h2" sx={{ fontSize: '1.0625rem', mb: 1 }}>
        {t.companyProfile.confirmTitle}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }} data-testid="perfil-confirmacao">
        {perfil.confirmedAt && perfil.confirmedBy
          ? `${t.companyProfile.confirmedBy} ${perfil.confirmedBy} ${t.companyProfile.confirmedAt} ${quando}`
          : t.companyProfile.confirmNone}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: podeConfirmar ? 1 : 0 }}>
        {t.companyProfile.confirmHelp}
      </Typography>
      {podeConfirmar && (
        <>
          <Button variant="contained" disabled={sujo || vazio || confirmando} onClick={onConfirmar}>
            {t.companyProfile.confirmButton}
          </Button>
          {sujo && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
              {t.companyProfile.confirmUnsaved}
            </Typography>
          )}
        </>
      )}
    </Paper>
  )
}

function ListaFechada({
  rotulo, opcoes, itens, pode, onChange,
}: {
  rotulo: string
  opcoes: [string, string][]
  itens: string[]
  pode: boolean
  onChange: (v: string[]) => void
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
        {rotulo}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {opcoes.map(([v, r]) => {
          const marcado = itens.includes(v)
          return (
            <Chip
              key={v}
              label={r}
              color={marcado ? 'info' : 'default'}
              variant={marcado ? 'filled' : 'outlined'}
              aria-pressed={marcado}
              disabled={!pode && !marcado}
              clickable={pode}
              onClick={pode ? () => onChange(marcado ? itens.filter((x) => x !== v) : [...itens, v]) : undefined}
            />
          )
        })}
      </Box>
    </Box>
  )
}

export function CompanyProfilePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const voltar = () => (location.key !== 'default' ? navigate(-1) : navigate('/'))
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe })
  const tenantId = getWorkTenantId() ?? me.data?.tenantId ?? ''
  const pode = (me.data?.permissions ?? []).includes(COMPANY_PROFILE_WRITE_PERMISSION)
  const podeConfirmar = (me.data?.permissions ?? []).includes(COMPANY_PROFILE_CONFIRM_PERMISSION)
  const perfil = useQuery({
    queryKey: companyProfileKey(tenantId),
    queryFn: () => fetchCompanyProfile(tenantId),
    enabled: tenantId !== '',
  })
  const [f, setF] = useState<ProfileWithStatus | null>(null)
  const [sujo, setSujo] = useState(false)
  const [salvo, setSalvo] = useState(false)
  useEffect(() => {
    if (perfil.data) {
      setF(perfil.data)
      setSujo(false)
    }
  }, [perfil.data])
  const salvar = useMutation({
    mutationFn: (p: CompanyProfile) => saveCompanyProfile(tenantId, p),
    onSuccess: (p) => {
      qc.setQueryData(companyProfileKey(tenantId), p)
      setSalvo(true)
    },
  })
  const confirmar = useMutation({
    mutationFn: () => confirmCompanyProfile(tenantId),
    onSuccess: (p) => qc.setQueryData(companyProfileKey(tenantId), p),
  })
  const muda = <K extends keyof CompanyProfile>(k: K, v: CompanyProfile[K]) => {
    setSalvo(false)
    setSujo(true)
    setF((atual) => (atual ? { ...atual, [k]: v } : atual))
  }
  // s67 / PE2: o resultado colado vira RASCUNHO no formulario (so os campos lidos; as fontes lidas somam as que ha)
  const aplicaRascunho = (r: ResultadoLido) => {
    if (r.lidos === 0) return
    setSalvo(false)
    setSujo(true)
    setF((atual) => (atual ? { ...atual, ...r.rascunho, fontes: { ...atual.fontes, ...r.fontes } } : atual))
  }
  const texto = (k: 'ramo' | 'cidade' | 'atividades', rotulo: string, max: number, multi = false) => (
    <TextField
      size="small"
      fullWidth
      label={rotulo}
      value={f?.[k] ?? ''}
      disabled={!pode}
      multiline={multi}
      inputProps={{ maxLength: max }}
      onChange={(e) => muda(k, e.target.value)}
      InputLabelProps={{ shrink: true }}
      id={`perfil-${k}`}
    />
  )
  const escolha = (k: 'setor' | 'porte' | 'uf', rotulo: string, opcoes: [string, string][]) => (
    <TextField
      select
      size="small"
      fullWidth
      label={rotulo}
      value={f?.[k] ?? ''}
      disabled={!pode}
      onChange={(e) => muda(k, e.target.value || null)}
      InputLabelProps={{ shrink: true }}
      id={`perfil-${k}`}
    >
      <MenuItem value="">{t.companyProfile.none}</MenuItem>
      {opcoes.map(([v, r]) => (
        <MenuItem key={v} value={v}>
          {r}
        </MenuItem>
      ))}
    </TextField>
  )

  return (
    <Stack spacing={2} sx={{ maxWidth: 820 }}>
      <Box>
        <Button size="small" startIcon={<ArrowBack fontSize="small" />} onClick={voltar}>
          {t.companyProfile.back}
        </Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessOutlined color="primary" />
        <Typography variant="h1" sx={{ fontSize: '1.5rem' }}>
          {t.companyProfile.title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {pode ? t.companyProfile.introEdit : t.companyProfile.introRead}
      </Typography>
      {perfil.isError ? (
        <ErrorState error={perfil.error} />
      ) : !f ? (
        <LoadingState />
      ) : (
        <>
          {pode && <Pesquisa onLido={aplicaRascunho} />}
          <Paper variant="outlined" component="section" sx={{ p: 2 }}>
            <Typography variant="h2" sx={{ fontSize: '1.0625rem', mb: 1.5 }}>
              {t.companyProfile.basics}
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              {escolha('setor', t.companyProfile.setor, SETORES)}
              {texto('ramo', t.companyProfile.ramo, 80)}
              {escolha('uf', t.companyProfile.uf, UFS.map((u) => [u, u]))}
              {texto('cidade', t.companyProfile.cidade, 80)}
              {escolha('porte', t.companyProfile.porte, PORTES)}
            </Box>
            <Box sx={{ mt: 1.5 }}>{texto('atividades', t.companyProfile.atividades, 200, true)}</Box>
          </Paper>
          <Paper variant="outlined" component="section" sx={{ p: 2 }}>
            <Typography variant="h2" sx={{ fontSize: '1.0625rem', mb: 1.5 }}>
              {t.companyProfile.whatItDoes}
            </Typography>
            <Stack spacing={2}>
              <ListaLivre rotulo={t.companyProfile.produtos} novo={t.companyProfile.newProduto} itens={f.produtos} limite={80} pode={pode} onChange={(v) => muda('produtos', v)} />
              <ListaLivre rotulo={t.companyProfile.processos} novo={t.companyProfile.newProcesso} itens={f.processos} limite={80} pode={pode} onChange={(v) => muda('processos', v)} />
            </Stack>
          </Paper>
          <Paper variant="outlined" component="section" sx={{ p: 2 }}>
            <Typography variant="h2" sx={{ fontSize: '1.0625rem', mb: 1.5 }}>
              {t.companyProfile.howItWorks}
            </Typography>
            <Stack spacing={2}>
              <Locais locais={f.locais} antigo={f.unidades} pode={pode} onChange={(v) => muda('locais', v)} />
              <ListaFechada rotulo={t.companyProfile.turnos} opcoes={TURNOS} itens={f.turnos} pode={pode} onChange={(v) => muda('turnos', v)} />
              <ListaFechada rotulo={t.companyProfile.mercado} opcoes={MERCADOS} itens={f.mercado} pode={pode} onChange={(v) => muda('mercado', v)} />
              <ListaLivre rotulo={t.companyProfile.certificacoes} novo={t.companyProfile.newCertificacao} itens={f.certificacoes} limite={60} pode={pode} onChange={(v) => muda('certificacoes', v)} />
            </Stack>
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t.companyProfile.warning}
            </Alert>
          </Paper>
          {Object.keys(f.fontes).length > 0 && (
            <Paper variant="outlined" component="section" sx={{ p: 2 }}>
              <Typography variant="h2" sx={{ fontSize: '1.0625rem', mb: 1 }}>
                {t.companyProfile.sources}
              </Typography>
              <Stack spacing={0.5}>
                {Object.entries(f.fontes).map(([campo, url]) => (
                  <Box key={campo} sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="fonte">
                    <Typography variant="body2" sx={{ minWidth: 140 }}>{ROTULO_CAMPO[campo] ?? campo}</Typography>
                    <Link href={url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ wordBreak: 'break-all', flexGrow: 1 }}>
                      {url}
                    </Link>
                    {pode && (
                      <IconButton size="small" aria-label={`${t.companyProfile.removeSource} ${ROTULO_CAMPO[campo] ?? campo}`}
                        onClick={() => {
                          const resto = { ...f.fontes }
                          delete resto[campo]
                          muda('fontes', resto)
                        }}>
                        <CloseOutlined fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
          <Confirmacao perfil={f} podeConfirmar={podeConfirmar} sujo={sujo} vazio={preenchidos(f) === 0}
            onConfirmar={() => confirmar.mutate()} confirmando={confirmar.isPending} />
          {confirmar.isError && <ErrorState error={confirmar.error} />}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {`${t.companyProfile.filled} ${preenchidos(f)} ${t.companyProfile.of} ${PROFILE_FIELDS.length}`}
            </Typography>
            {pode && (
              <Button variant="contained" disabled={salvar.isPending} onClick={() => salvar.mutate(f)}>
                {t.companyProfile.save}
              </Button>
            )}
          </Box>
          {salvo && <Alert severity="success">{t.companyProfile.saved}</Alert>}
          {salvar.isError && <ErrorState error={salvar.error} />}
        </>
      )}
    </Stack>
  )
}
